/**
 * Client for the Token Filter Cloudflare Worker.
 *
 * Accepts token identifiers, enriches them via CoinGecko liquidity data,
 * and returns which tokens pass filtering (allow-list, liquidity threshold).
 *
 * Fails open: if the service is unreachable, all tokens are returned as-is.
 */

import { TOKEN_SERVICE_URL } from "@/consts/token-service";

const DEVMODE_SHARED_SECRET = process.env.NEXT_PUBLIC_TOKEN_FILTER_DEVMODE_SHARED_SECRET;

export interface TokenFilterInput {
  chainId: number;
  address: string;
  symbol?: string | null;
  /** True when upstream (SimDune) enriched the token with a `price_usd` — exempts it from `no_coingecko_listing`. */
  hasSimDunePrice?: boolean;
}

export interface FilteredTokenEntry {
  token: TokenFilterInput;
  reasons: string[];
}

export interface TokenFilterResponse {
  tokens: TokenFilterInput[];
  filtered: {
    count: number;
    tokens: FilteredTokenEntry[];
  };
}

export interface TokenFilterOptions {
  minLiquidity?: number;
  allowAddresses?: string[];
  /** Require a CoinGecko coin id (in addition to SimDune enrichment). Spam tokens fail both. */
  requireCoingeckoListing?: boolean;
}

/**
 * Call the token filter service to determine which tokens pass filtering.
 *
 * Returns all tokens unfiltered if the service returns an error (fail-open).
 */
export async function filterTokens(
  tokens: TokenFilterInput[],
  options?: TokenFilterOptions,
): Promise<TokenFilterResponse> {
  if (tokens.length === 0) {
    return { tokens, filtered: { count: 0, tokens: [] } };
  }

  const url = new URL(`${TOKEN_SERVICE_URL}/filter`);
  if (options?.minLiquidity !== undefined) {
    url.searchParams.set("minLiquidity", options.minLiquidity.toString());
  }
  if (options?.allowAddresses && options.allowAddresses.length > 0) {
    url.searchParams.set("allowAddresses", options.allowAddresses.join(","));
  }
  if (options?.requireCoingeckoListing) {
    url.searchParams.set("requireCg", "true");
  }

  try {
    if (DEVMODE_SHARED_SECRET) {
      url.searchParams.set("localkey", DEVMODE_SHARED_SECRET);
    }

    const response = await fetch(url.toString(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tokens }),
    });

    if (!response.ok) {
      console.warn("[tokenFilter] Service returned error, bypassing filter:", response.status);
      return { tokens, filtered: { count: 0, tokens: [] } };
    }

    return response.json();
  } catch (error) {
    console.warn("[tokenFilter] Service unreachable, bypassing filter:", error);
    return { tokens, filtered: { count: 0, tokens: [] } };
  }
}
